import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { IncidentSeverity } from '@/types/incident';
import { useServicesStore } from '@/stores/servicesStore';
import { useUsersStore } from '@nimbitlabs/features';
import { useIncidentsStore } from '@/stores/incidentsStore';
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Form, FormControl, FormField, FormItem, FormLabel, FormMessage, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea, useToast } from '@nimbitlabs/ui';

const incidentSchema = z.object({
  title: z.string().min(1, 'Incident title is required').max(200, 'Title must be 200 characters or less'),
  description: z.string().min(1, 'Description is required').max(2000, 'Description must be 2000 characters or less'),
  severity: z.nativeEnum(IncidentSeverity, { required_error: 'Please select a severity level' }),
  serviceId: z.string().optional(),
  environmentId: z.string().optional(),
  assignedToUserId: z.string().optional(),
});

type IncidentFormData = z.infer<typeof incidentSchema>;

interface CreateIncidentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onIncidentCreated: () => void;
}

export const CreateIncidentModal: React.FC<CreateIncidentModalProps> = ({
  open,
  onOpenChange,
  onIncidentCreated,
}) => {
  const { services } = useServicesStore();
  const { users } = useUsersStore();
  const { addIncident } = useIncidentsStore();
  const { toast } = useToast();
  
  const form = useForm<IncidentFormData>({
    resolver: zodResolver(incidentSchema),
    defaultValues: {
      title: '',
      severity: IncidentSeverity.Medium,
      description: '',
      serviceId: undefined,
      environmentId: undefined,
      assignedToUserId: undefined,
    },
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const onSubmit = async (data: IncidentFormData) => {
    try {
      // Convert severity to lowercase for API
      const payload = {
        ...data,
        severity: data.severity.toLowerCase() as any,
      };
      await addIncident(payload);
      toast({ title: 'Success', description: 'Incident created successfully' });
      onIncidentCreated();
      onOpenChange(false);
      form.reset();
    } catch (error) {
      console.error('Failed to create incident:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create incident',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Incident</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title *</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Brief description of the incident" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="severity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Severity *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={IncidentSeverity.Low}>Low</SelectItem>
                      <SelectItem value={IncidentSeverity.Medium}>Medium</SelectItem>
                      <SelectItem value={IncidentSeverity.High}>High</SelectItem>
                      <SelectItem value={IncidentSeverity.Critical}>Critical</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description *</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Detailed description of the incident, impact, and any relevant information"
                      rows={4}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="serviceId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || 'none'}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="assignedToUserId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Assign To (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || 'unassigned'}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a user" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name || user.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Creating...' : 'Create Incident'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
